import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, ArrowLeft } from "lucide-react";
import MadeByBadge from "@/components/brand/MadeByBadge";

/** Email verification page — 6-digit OTP UI with autotab + paste. */
export default function VerifyEmailPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { refreshUser } = useAuth();
  const email = useMemo(() => {
    const q = new URLSearchParams(loc.search);
    return (q.get("email") || "").toLowerCase();
  }, [loc.search]);

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);
  const refs = useRef([]);

  useEffect(() => {
    if (!email) nav("/register", { replace: true });
    // Start with initial 30s cooldown (server just sent one during register)
    setCooldown(30);
    // Auto-focus first input
    setTimeout(() => refs.current[0]?.focus(), 100);
  }, [email, nav]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleChange = (i, v) => {
    v = v.replace(/[^0-9]/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
    if (v && i < 5) refs.current[i + 1]?.focus();
    setError("");
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const txt = (e.clipboardData?.getData("text") || "").replace(/[^0-9]/g, "").slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) next[i] = txt[i];
    setDigits(next);
    const nextIdx = Math.min(5, txt.length);
    refs.current[nextIdx]?.focus();
    setError("");
  };

  const submit = async () => {
    const code = digits.join("");
    if (code.length !== 6) return setError("6 haneli kodu tam gir");
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/verify-email", { email, code });
      // The backend sets our JWT cookie in the response — refresh user then go
      await refreshUser?.();
      nav("/app/chats", { replace: true });
    } catch (e) {
      setError(formatApiError(e));
      setDigits(["", "", "", "", "", ""]);
      refs.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setError("");
    setResent(false);
    try {
      await api.post("/auth/resend-code", { email });
      setCooldown(30);
      setResent(true);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-[#D4B3FF]" data-testid="verify-email-page">
      <div className="w-full max-w-md bg-white border-hard shadow-hard-lg rounded-2xl p-6 md:p-8">
        <Link
          to="/register"
          data-testid="verify-back-btn"
          className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[#555] mb-3 press-btn"
        >
          <ArrowLeft strokeWidth={3} className="w-3.5 h-3.5" />
          Geri
        </Link>

        <div className="w-14 h-14 rounded-2xl border-hard shadow-hard bg-[#CCFF00] flex items-center justify-center mb-4">
          <Mail strokeWidth={2.5} className="w-7 h-7" />
        </div>

        <h1 className="font-display text-3xl md:text-4xl leading-tight mb-1">Kodu gir</h1>
        <p className="text-sm text-[#333]">
          <b>{email}</b> adresine 6 haneli bir kod gönderdik. Kutu görünmüyorsa spam klasörünü kontrol et.
        </p>

        <div
          className="flex justify-between gap-1.5 md:gap-2 my-6"
          onPaste={handlePaste}
          data-testid="verify-code-input"
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (refs.current[i] = el)}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              data-testid={`verify-digit-${i}`}
              className="w-11 md:w-12 h-14 md:h-16 text-center text-2xl md:text-3xl font-black bg-[#F9F8F6] border-hard rounded-xl focus:outline-none focus:bg-white focus:border-[#CCFF00] focus:border-[3px]"
            />
          ))}
        </div>

        {error && (
          <div className="text-sm font-bold bg-[#FF9E80] border-hard-2 rounded-lg px-3 py-2 mb-3" data-testid="verify-error">
            {error}
          </div>
        )}
        {resent && !error && (
          <div className="text-sm font-bold bg-[#CCFF00] border-hard-2 rounded-lg px-3 py-2 mb-3" data-testid="verify-resent">
            Yeni kod yolda ✓
          </div>
        )}

        <button
          onClick={submit}
          disabled={submitting || digits.join("").length !== 6}
          data-testid="verify-submit-btn"
          className="w-full bg-[#0A0A0A] text-white font-black border-hard shadow-hard rounded-xl px-5 py-3 press-btn disabled:opacity-60"
        >
          {submitting ? "Doğrulanıyor…" : "Doğrula"}
        </button>

        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs text-[#555] font-bold">Kod gelmedi mi?</span>
          <button
            onClick={resend}
            disabled={cooldown > 0}
            data-testid="verify-resend-btn"
            className="text-xs font-black uppercase tracking-widest press-btn disabled:opacity-50 underline"
          >
            {cooldown > 0 ? `Tekrar gönder (${cooldown}sn)` : "Tekrar gönder"}
          </button>
        </div>
        <div className="mt-6 flex justify-center">
          <MadeByBadge />
        </div>
      </div>
    </div>
  );
}
